/**
 * Staff queries and mutations.
 *
 * The only place that knows the user endpoints exist.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type {
  DepartmentSummary,
  RoleRead,
  UserCreate,
  UserCreated,
  UserPage,
  UserRead,
  UserStatus,
  UserUpdate,
} from '@/api/schema';
import { apiUrl } from '@/lib/env';

export type UserFilters = {
  page: number;
  per_page: number;
  search: string;
  status: UserStatus | '';
  department_id: string;
  sort_by: string;
  sort_dir: 'asc' | 'desc';
};

export const USERS_KEY = ['users'] as const;

/** Everything that depends on the staff list, refreshed after any change. */
function invalidateUsers(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: USERS_KEY });
}

export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: [...USERS_KEY, 'list', filters],
    queryFn: () => api.get<UserPage>('/api/v1/users', { query: { ...filters } }),
    // Keeps the previous page on screen while the next one loads, so paging and
    // filtering do not flash an empty table.
    placeholderData: (previous) => previous,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleRead[]>('/api/v1/roles'),
    // Three fixed roles that almost never change.
    staleTime: 5 * 60_000,
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<DepartmentSummary[]>('/api/v1/departments'),
    staleTime: 5 * 60_000,
  });
}

/** The photo endpoint, or null. Served by the API, never a public URL. */
export function photoUrlFor(user: Pick<UserRead, 'id' | 'photo_path'>): string | null {
  return user.photo_path ? apiUrl(`/api/v1/users/${user.id}/photo`) : null;
}

// --------------------------------------------------------------------------- //
// Creating and editing
// --------------------------------------------------------------------------- //
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreate) => api.post<UserCreated>('/api/v1/users', body),
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UserUpdate & { id: string }) =>
      api.patch<UserRead>(`/api/v1/users/${id}`, body),
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useUploadPhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload<UserRead>(`/api/v1/users/${id}/photo`, form);
    },
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useRemovePhoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<UserRead>(`/api/v1/users/${id}/photo`),
    onSuccess: () => invalidateUsers(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Status
// --------------------------------------------------------------------------- //
/**
 * Activate or deactivate one account, applied to the table immediately.
 *
 * Optimistic because the outcome is a single known value and the request is short;
 * waiting for a round trip to flip one chip feels broken. The previous page is kept
 * and put back if the server refuses, so a failure is never left looking like a
 * success (plan Phase 9 exit criteria).
 */
export function useSetUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'deactivated' }) =>
      api.post<UserRead>(
        `/api/v1/users/${id}/${status === 'active' ? 'activate' : 'deactivate'}`,
      ),

    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: USERS_KEY });
      const snapshot = queryClient.getQueriesData<UserPage>({ queryKey: USERS_KEY });

      queryClient.setQueriesData<UserPage>({ queryKey: USERS_KEY }, (page) =>
        page
          ? {
              ...page,
              items: page.items.map((user) => (user.id === id ? { ...user, status } : user)),
            }
          : page,
      );

      return { snapshot };
    },

    onError: (_error, _variables, context) => {
      // Put every page back exactly as it was.
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },

    // Whatever happened, the server is the authority on what the row now says.
    onSettled: () => invalidateUsers(queryClient),
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/users/${id}`);
    },
    onSuccess: () => invalidateUsers(queryClient),
  });
}

// --------------------------------------------------------------------------- //
// Invitations
// --------------------------------------------------------------------------- //
export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ token: string; expires_at: string }>(`/api/v1/users/${id}/resend-invite`),
    onSuccess: () => invalidateUsers(queryClient),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ token: string; expires_at: string }>(`/api/v1/users/${id}/reset-password`),
  });
}

// --------------------------------------------------------------------------- //
// Bulk
// --------------------------------------------------------------------------- //
export type BulkAction = 'activate' | 'deactivate' | 'remove';

/**
 * One request, one transaction.
 *
 * Not optimistic: the server may refuse the whole set (an invited account in an
 * activate selection, or your own account in a remove), and pretending otherwise
 * would show ten rows changing and then snapping back.
 */
export function useBulkAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ids }: { action: BulkAction; ids: string[] }) =>
      api.post<{ affected: number }>(`/api/v1/users/bulk/${action}`, { user_ids: ids }),
    onSettled: () => invalidateUsers(queryClient),
  });
}
